"use client";

import { useState } from "react";

export default function JohariAdjectiveSelection({ adjectives, onComplete }) {
    const [selectedAdjectives, setSelectedAdjectives] = useState([]);
    const minSelection = 5;
    const maxSelection = 20;

    const toggleAdjective = (adjective) => {
        setSelectedAdjectives((prev) => {
            if (prev.includes(adjective)) {
                return prev.filter((a) => a !== adjective);
            } else if (prev.length < maxSelection) {
                return [...prev, adjective];
            }
            return prev;
        });
    };

    const handleComplete = () => {
        if (selectedAdjectives.length >= minSelection) {
            const userId = Math.random().toString(36).substring(2, 15);
            localStorage.setItem(`johari_self_${userId}`, JSON.stringify(selectedAdjectives));
            onComplete(selectedAdjectives, userId);
        }
    };

    const progress = Math.min((selectedAdjectives.length / minSelection) * 100, 100);
    const canComplete = selectedAdjectives.length >= minSelection;

    return (
        <div className="space-y-6  mx-auto" style={{width:'85%'}}>
            {/* Header */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <h2 className="text-lg font-semibold text-blue-800 mb-1">
                    Which of the following adjectives do you think best describes You?
                </h2>
                <p className="text-sm text-blue-600">
                    Select between {minSelection} and {maxSelection} adjectives.
                </p>
            </div>

            {/* Progress */}
            <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                    <span>{selectedAdjectives.length} Selected</span>
                    <span>
                        {selectedAdjectives.length < minSelection
                            ? `${minSelection - selectedAdjectives.length} more needed`
                            : `${maxSelection - selectedAdjectives.length} remaining`}
                    </span>
                </div>
                <div className="w-full bg-gray-200 h-2 rounded overflow-hidden">
                    <div
                        className="bg-blue-600 h-2"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-wrap gap-2 justify-center">
                    {adjectives.map((adjective) => {
                        const isSelected = selectedAdjectives.includes(adjective);
                        const isDisabled = !isSelected && selectedAdjectives.length >= maxSelection;

                        return (
                            <button
                                key={adjective}
                                onClick={() => toggleAdjective(adjective)}
                                disabled={isDisabled}
                                className={`px-3 py-1 text-sm rounded border transition 
                  ${isSelected ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700" : "border-gray-300 text-gray-700 hover:bg-blue-50"} 
                  ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                {adjective}
                            </button>
                        );
                    })}
                </div>
            </div>

            {selectedAdjectives.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="text-green-800 font-semibold mb-2">Your Selected Adjectives</h3>
                    <div className="flex flex-wrap gap-2">
                        {selectedAdjectives.map((adjective) => (
                            <div
                                key={adjective}
                                className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full flex items-center gap-1"
                            >
                                {adjective}
                                <button
                                    onClick={() => toggleAdjective(adjective)}
                                    className="ml-1 text-green-600 hover:text-green-800"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="text-center">
                <button
                    onClick={handleComplete}
                    disabled={!canComplete}
                    className={`px-5 py-2 text-white text-sm font-medium rounded 
            ${canComplete ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-300 cursor-not-allowed"}`}
                >
                    {canComplete
                        ? "Complete Self-Assessment"
                        : `Select ${minSelection - selectedAdjectives.length} More`}
                </button>
            </div>

            <p className="text-center text-sm text-gray-500">
                {selectedAdjectives.length} Selected.
            </p>
        </div>
    );
}
